/**
 * @file Header.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Header component.
 */
const vHeader = () => {
	const header = document.querySelector('.v-header');

	if (!header) {
		return;
	}

	gsap.registerPlugin(ScrollTrigger);

	const mm = gsap.matchMedia();

	const headerGradient = document.querySelector('.v-header-gradient');

	// header.classList.add('v-header--fixed');
	headerGradient.classList.add('v-header-gradient--fixed');

	const utilityTrigger = document.querySelector(
		'.v-header__utility-nav-trigger',
	);

	const searchTrigger = document.querySelector('.v-header__search-trigger');
	const searchClose = document.querySelector('.v-header__search-close');
	const searchField = document.querySelector(
		'.v-header__search-bar .v-search-form__field',
	);

	const stuckVars = {};

	/**
	 * Set stuck vars.
	 */
	const setStuckVars = () => {
		stuckVars['--link-default'] = gsap.getProperty(
			header,
			'--header-link-default',
		);
		stuckVars['--link-hover'] = gsap.getProperty(header, '--header-link-hover');
		stuckVars['--cta-text'] = gsap.getProperty(header, '--header-cta-text');
		stuckVars['--cta-text-hover'] = gsap.getProperty(
			header,
			'--header-cta-text-hover',
		);
		stuckVars['--cta-background'] = gsap.getProperty(
			header,
			'--header-cta-background',
		);
		stuckVars['--cta-background-hover'] = gsap.getProperty(
			header,
			'--header-cta-background-hover',
		);
		stuckVars['--cta-border'] = gsap.getProperty(header, '--header-cta-border');
		stuckVars['--cta-border-hover'] = gsap.getProperty(
			header,
			'--header-cta-border-hover',
		);
		stuckVars['--cta-default'] = gsap.getProperty(
			header,
			'--header-cta-default',
		);
		stuckVars['--outline-cta-text'] = gsap.getProperty(
			header,
			'--header-outline-cta-text',
		);
		stuckVars['--outline-cta-text-hover'] = gsap.getProperty(
			header,
			'--header-outline-cta-text-hover',
		);
		stuckVars['--outline-cta-background'] = gsap.getProperty(
			header,
			'--header-outline-cta-background',
		);
		stuckVars['--outline-cta-background-hover'] = gsap.getProperty(
			header,
			'--header-outline-cta-background-hover',
		);
		stuckVars['--outline-cta-border'] = gsap.getProperty(
			header,
			'--header-outline-cta-border',
		);
		stuckVars['--outline-cta-border-hover'] = gsap.getProperty(
			header,
			'--header-outline-cta-border-hover',
		);
		stuckVars['--outline-cta-default'] = gsap.getProperty(
			header,
			'--header-outline-cta-default',
		);
		stuckVars['--logo'] = gsap.getProperty(header, '--header-logo');
		stuckVars['--logo2'] = gsap.getProperty(header, '--header-logo2');
		stuckVars['--search'] = gsap.getProperty(header, '--header-search');
		stuckVars['--search-hover'] = gsap.getProperty(
			header,
			'--header-search-hover',
		);
		stuckVars['--menu-trigger-border'] = gsap.getProperty(
			header,
			'--header-menu-trigger-border',
		);
		stuckVars['--menu-trigger-fill'] = gsap.getProperty(
			header,
			'--header-menu-trigger-fill',
		);
		stuckVars['--menu-trigger-hover'] = gsap.getProperty(
			header,
			'--header-menu-trigger-hover',
		);
		stuckVars['--menu-trigger-bar'] = gsap.getProperty(
			header,
			'--header-menu-trigger-bar',
		);
		stuckVars['--menu-trigger-bar-hover'] = gsap.getProperty(
			header,
			'--header-menu-trigger-bar-hover',
		);
		stuckVars['--background'] = gsap.getProperty(header, '--header-background');
		stuckVars['--border-color'] = gsap.getProperty(
			header,
			'--header-border-color',
		);
		stuckVars['--gradient-opacity'] = gsap.getProperty(
			header,
			'--header-gradient-opacity',
		);
	};
	setStuckVars();

	/**
	 * Create header color changing timeline.
	 * @returns {gsap.core.Timeline} Header timeline.
	 */
	const headerColorTl = () =>
		gsap
			.timeline({
				defaults: {
					ease: 'sine.out',
					duration: 0.2,
				},
				scrollTrigger: {
					trigger: 'body',
					start: '20px top',
					end: '+=1px',
					toggleActions: 'play none none reverse',
					onRefresh: (st) => {
						if (st.progress === 1 && st.animation.progress() !== 1) {
							st.animation.progress(1);
						}
					},
					onLeaveBack: () => {
						header.classList.remove('v-header--stuck-color');
						header.classList.add('v-header--stick-color-transition');
					},
				},
				onStart: () => {
					header.classList.add('v-header--stick-color-transition');
				},
				onComplete: () => {
					header.classList.remove('v-header--stick-color-transition');
					header.classList.add('v-header--stuck-color');
					const e = new CustomEvent('v-sticky-header-color', {
						detail: {
							stuck: true,
						},
					});
					window.dispatchEvent(e);
				},
				onReverseComplete: () => {
					header.classList.remove('v-header--stick-color-transition');
					const e = new CustomEvent('v-sticky-header-color', {
						detail: {
							stuck: false,
						},
					});
					window.dispatchEvent(e);
				},
			})
			.addLabel('start')
			.to(header, {
				duration: 0.4,
				'--link-default': stuckVars['--link-default'],
				'--link-hover': stuckVars['--link-hover'],
				'--logo': stuckVars['--logo'],
				'--logo2': stuckVars['--logo2'],
				'--search': stuckVars['--search'],
				'--search-hover': stuckVars['--search-hover'],
				'--menu-trigger-border': stuckVars['--menu-trigger-border'],
				'--menu-trigger-fill': stuckVars['--menu-trigger-fill'],
				'--menu-trigger-hover': stuckVars['--menu-trigger-hover'],
				'--menu-trigger-bar': stuckVars['--menu-trigger-bar'],
				'--menu-trigger-bar-hover': stuckVars['--menu-trigger-bar-hover'],
				'--background': stuckVars['--background'],
				'--border-color': stuckVars['--border-color'],
				'--cta-text': stuckVars['--cta-text'],
				'--cta-text-hover': stuckVars['--cta-text-hover'],
				'--cta-background': stuckVars['--cta-background'],
				'--cta-background-hover': stuckVars['--cta-background-hover'],
				'--cta-border': stuckVars['--cta-border'],
				'--cta-default': stuckVars['--cta-default'],
				'--cta-border-hover': stuckVars['--cta-border-hover'],
				'--outline-cta-text': stuckVars['--outline-cta-text'],
				'--outline-cta-text-hover': stuckVars['--outline-cta-text-hover'],
				'--outline-cta-background': stuckVars['--outline-cta-background'],
				'--outline-cta-background-hover':
					stuckVars['--outline-cta-background-hover'],
				'--outline-cta-border': stuckVars['--outline-cta-border'],
				'--outline-cta-default': stuckVars['--outline-cta-default'],
				'--outline-cta-border-hover': stuckVars['--outline-cta-border-hover'],
			})
			.to(
				headerGradient,
				{
					duration: 0.4,
					'--gradient-opacity': stuckVars['--gradient-opacity'],
				},
				'<',
			);

	/**
	 * Create header timeline.
	 * @returns {gsap.core.Timeline} Header timeline.
	 */
	const headerTl = () =>
		gsap
			.timeline({
				defaults: {
					ease: 'sine.out',
					duration: 0.2,
				},
				scrollTrigger: {
					trigger: 'body',
					start: '20px top',
					end: '+=1px',
					toggleActions: 'play none none reverse',
					onRefresh: (st) => {
						if (st.progress === 1 && st.animation.progress() !== 1) {
							st.animation.progress(1);
						}
					},
					onLeaveBack: () => {
						header.classList.remove('v-header--stuck');
						header.classList.remove('v-header--fixed');
						header.classList.add('v-header--stick-transition');
					},
				},
				onStart: () => {
					header.classList.add('v-header--stick-transition');
					header.classList.add('v-header--fixed');
				},
				onComplete: () => {
					header.classList.remove('v-header--stick-transition');
					header.classList.add('v-header--stuck');
					const e = new CustomEvent('v-sticky-header', {
						detail: {
							stuck: true,
						},
					});
					window.dispatchEvent(e);
				},
				onReverseComplete: () => {
					header.classList.remove('v-header--stick-transition');
					gsap.set('.v-header__wrap', { clearProps: 'all' });
					const e = new CustomEvent('v-sticky-header', {
						detail: {
							stuck: false,
						},
					});
					window.dispatchEvent(e);
				},
			})
			.addLabel('start');

	/**
	 * Match media callback.
	 * @returns {Function} Callback function.
	 */
	const mobileTabletSizes = () => {
		setStuckVars();
		const headerTimeline = headerTl()
			.to(
				[header, headerGradient],
				{
					y: -16,
					duration: 0.4,
				},
				'start',
			)
			.to(
				'.v-header__wrap',
				{
					y: 8,
					duration: 0.4,
				},
				'<',
			);

		const colorTimeline = headerColorTl();

		/**
		 * Disable scroll trigger.
		 * @param {Event} ev - Mobile menu toggle event.
		 */
		const disableScrollTrigger = (ev) => {
			if (ev.detail.open) {
				headerTimeline.scrollTrigger.disable(false);
				colorTimeline.scrollTrigger.disable(false);
				headerTimeline.reverse();
			} else {
				headerTimeline.scrollTrigger.enable();
				colorTimeline.scrollTrigger.enable();
			}
		};

		window.addEventListener('v-mobile-menu-toggle', disableScrollTrigger);

		return () => {
			if (
				!header
					.querySelector('.v-mobile-menu')
					.classList.contains('v-mobile-menu--open')
			) {
				gsap.set(header, { clearProps: 'all' });
			}
			window.removeEventListener('v-mobile-menu-toggle', disableScrollTrigger);
		};
	};

	mm.add('(max-width: 549px)', () => {
		setStuckVars();
		const colorTimeline = headerColorTl();

		/**
		 * Disable scroll trigger.
		 * @param {Event} ev - Mobile menu toggle event.
		 */
		const disableScrollTrigger = (ev) => {
			if (ev.detail.open) {
				colorTimeline.scrollTrigger.disable(false);
			} else {
				colorTimeline.scrollTrigger.enable();
			}
		};

		window.addEventListener('v-mobile-menu-toggle', disableScrollTrigger);

		return () => {
			if (
				!header
					.querySelector('.v-mobile-menu')
					.classList.contains('v-mobile-menu--open')
			) {
				gsap.set(header, { clearProps: 'all' });
			}
			window.removeEventListener('v-mobile-menu-toggle', disableScrollTrigger);
		};
	});
	mm.add('(min-width: 550px) and (max-width: 767px)', mobileTabletSizes);
	mm.add('(min-width: 768px) and (max-width: 1159px)', mobileTabletSizes);

	mm.add('(min-width: 1160px) and (max-width: 1439px)', () => {
		setStuckVars();
		const headerTimeline = headerTl()
			.to(
				[header, headerGradient],
				{
					y: -40,
					duration: 0.4,
				},
				'start',
			)
			.to(
				'.v-header__wrap',
				{
					y: 20,
					duration: 0.4,
				},
				'<',
			)
			.to(
				'.v-main-menu__dropdown',
				{
					y: -20,
					duration: 0.4,
				},
				'<',
			);

		const colorTimeline = headerColorTl();

		if (header.classList.contains('v-header--dropdown-open')) {
			colorTimeline.progress(1);
		}

		/**
		 * Disable scroll trigger.
		 * @param {Event} ev - Mobile menu toggle event.
		 */
		const disableScrollTrigger = (ev) => {
			if (ev.detail.open) {
				headerTimeline.scrollTrigger.disable(false);
				colorTimeline.scrollTrigger.disable(false);
				headerTimeline.reverse();
			} else {
				headerTimeline.scrollTrigger.enable();
				colorTimeline.scrollTrigger.enable();
			}
		};

		window.addEventListener('v-mobile-menu-toggle', disableScrollTrigger);

		/**
		 * Disable color trigger.
		 * @param {Event} ev - Mobile menu toggle event.
		 */
		const disableColorTrigger = (ev) => {
			if (ev.detail.open) {
				colorTimeline.scrollTrigger.disable(false);
				colorTimeline.play();
			} else {
				colorTimeline.scrollTrigger.enable();
				if (window.scrollY < colorTimeline.scrollTrigger.start) {
					colorTimeline.reverse();
				}
			}
		};

		window.addEventListener('v-main-menu', disableColorTrigger);

		return () => {
			if (
				!header
					.querySelector('.v-mobile-menu')
					.classList.contains('v-mobile-menu--open')
			) {
				gsap.set(header, { clearProps: 'all' });
			}
			window.removeEventListener('v-mobile-menu-toggle', disableScrollTrigger);
			window.removeEventListener('v-main-menu', disableColorTrigger);
		};
	});

	mm.add('(min-width: 1440px)', () => {
		setStuckVars();
		const utilityMenuHeight = gsap.getProperty(
			'.v-header__utility-menu',
			'height',
		);
		const headerWrapGap = gsap.getProperty('.v-header__wrap', 'row-gap');
		const buttonsGap = gsap.getProperty('.v-header__buttons', 'column-gap');
		const utilityNavTriggerPush =
			gsap.getProperty('.v-header__utility-nav-trigger', 'width') +
			gsap.getProperty('.v-header__utility-nav-trigger', 'marginLeft') +
			buttonsGap;
		/* const mainNavPush =
			gsap.getProperty('.v-header__logo-link', 'width') * 0.12121212; */
		// const fullLogo = document.getElementById('full-logo');
		// const logoIcon = document.getElementById('logo-icon');

		const headerTimeline = headerTl()
			.to(
				[header, headerGradient],
				{
					y: -utilityMenuHeight - headerWrapGap - 32,
					duration: 0.4,
				},
				'start',
			)
			.to(
				'.v-header__search-bar',
				{
					y: utilityMenuHeight + headerWrapGap + 32,
					duration: 0.4,
				},
				'start',
			)
			.to(
				'.v-header__wrap',
				{
					y: 16,
					duration: 0.4,
					paddingTop: '1.5rem',
					paddingBottom: '1.5rem',
				},
				'<',
			)
			.to(
				'.v-main-menu__dropdown',
				{
					y: -16,
					duration: 0.4,
				},
				'<',
			)
			.to(
				'.v-header__utility-menu',
				{
					opacity: 0,
					y: -4,
				},
				'<',
			)
			.to(
				'.v-header__logo-link',
				{
					y: 0,
				},
				'<',
			)
			.to(
				'#full-logo #logo-text',
				{
					fill: '#ffffff',
					autoAlpha: 0,
					duration: 0.4,
				},
				'<',
			)
			.to(
				'.v-header__main-menu',
				{
					marginLeft: '-1.5rem',
				},
				'<',
			)
			.to(
				'.v-header__cta, .v-header__search-trigger',
				{
					x: 0 - utilityNavTriggerPush,
					duration: 0.4,
				},
				'<',
			)
			.to(
				'.v-header__utility-nav-trigger',
				{
					opacity: 1,
					onReverseComplete: () => {
						gsap.set(
							'.v-header__utility-nav-trigger-label, .v-header__utility-nav-trigger .v-header__menu-trigger',
							{
								pointerEvents: 'none',
							},
						);
					},
					onComplete: () => {
						gsap.set(
							'.v-header__utility-nav-trigger-label, .v-header__utility-nav-trigger .v-header__menu-trigger',
							{
								pointerEvents: 'all',
							},
						);
					},
				},
				'>',
			);

		const colorTimeline = headerColorTl();

		if (header.classList.contains('v-header--dropdown-open')) {
			colorTimeline.progress(1);
		}

		let disabledScrollY = 0;
		/**
		 * Re-enable scroll trigger.
		 */
		const reenableScrollTrigger = () => {
			if (disabledScrollY + 50 < window.scrollY || window.scrollY < 20) {
				headerTimeline.scrollTrigger.enable();
				window.removeEventListener('scroll', reenableScrollTrigger);
			}
		};

		/**
		 * Handle utility trigger click.
		 */
		const handleUtilityTrigger = () => {
			disabledScrollY = window.scrollY;
			headerTimeline.scrollTrigger.disable(false);
			headerTimeline.reverse();

			window.addEventListener('scroll', reenableScrollTrigger);
		};

		utilityTrigger.addEventListener('click', handleUtilityTrigger);

		const searchTl = gsap.to(header, {
			top: gsap.getProperty('.v-header__search-bar', '--search-height'),
			duration: 0.3,
			ease: 'sine.out',
			paused: true,
			onStart: () => {
				gsap.set('.v-header__search-bar', { display: 'block' });
			},
			onComplete: () => {
				searchField.focus();
			},
			onReverseComplete: () => {
				gsap.set('.v-header__search-bar', { display: 'none' });
			},
		});

		/**
		 * Handle search trigger click.
		 */
		const handeSearchTrigger = () => {
			searchTl.play();
		};
		searchTrigger.addEventListener('click', handeSearchTrigger);

		/**
		 * Handle search close click.
		 */
		const handleSearchClose = () => {
			searchTl.reverse();
		};
		searchClose.addEventListener('click', handleSearchClose);

		/**
		 * Disable color trigger.
		 * @param {Event} ev - Mobile menu toggle event.
		 */
		const disableColorTrigger = (ev) => {
			if (ev.detail.open) {
				colorTimeline.scrollTrigger.disable(false);
				colorTimeline.play();
			} else {
				colorTimeline.scrollTrigger.enable();
				if (window.scrollY < colorTimeline.scrollTrigger.start) {
					colorTimeline.reverse();
				}
			}
		};

		window.addEventListener('v-main-menu', disableColorTrigger);

		return () => {
			if (
				!header
					.querySelector('.v-mobile-menu')
					.classList.contains('v-mobile-menu--open')
			) {
				gsap.set(header, { clearProps: 'all' });
			}

			utilityTrigger.removeEventListener('click', handleUtilityTrigger);
			searchTrigger.removeEventListener('click', handeSearchTrigger);
			searchClose.removeEventListener('click', handleSearchClose);
			window.removeEventListener('scroll', reenableScrollTrigger);
			window.removeEventListener('v-main-menu', disableColorTrigger);
		};
	});
};

export default vHeader;
